import React, { useEffect, useRef, useState } from "react";
import { Check, Languages } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

/* Picks the UI language from the languages an admin has published (fed by
   GET /api/i18n/languages/published). Renders nothing until at least one
   language is published, so the app looks unchanged until then. English is
   always offered.

   Two looks, same behaviour:
   - default: a full-width native <select>, used inside the nav drawer (opens
     the OS picker on a phone, no portal/z-index trouble in the drawer).
   - variant="icon": a compact icon button with a dropdown panel, for the top
     bar next to the bookmark button (same size/shape/panel as BookmarkIcon). */
function useOptions() {
  const { lang, setLang, languages, t } = useLanguage();
  const options = [
    { code: "en", label: t("language.english"), sub: "" },
    ...languages.map((l) => ({ code: l.code, label: l.nativeName || l.name, sub: l.nativeName ? l.name : "" })),
  ];
  return { lang, setLang, options, hasLanguages: languages.length > 0, t };
}

function IconSwitcher() {
  const { lang, setLang, options, hasLanguages, t } = useOptions();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!hasLanguages) return null;

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center justify-center h-8 w-8 rounded-lg border transition ${
          lang !== "en" ? "border-forest-600/40 bg-forest-600/10 text-forest-700" : "border-sand-300 text-forest-700 hover:bg-sand-200"
        }`}
        aria-label={t("language.label")}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="language-menu-btn"
      >
        <Languages className="h-4 w-4" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-60 max-w-[min(85vw,calc(var(--app-frame-w)_-_2rem))] rounded-xl border border-sand-300 bg-white shadow-xl z-50 overflow-hidden"
          role="listbox"
          aria-label={t("language.label")}
          data-testid="language-menu-panel"
        >
          <div className="px-4 py-3 border-b border-sand-200">
            <span className="font-display font-semibold text-forest-700 text-sm">{t("language.label")}</span>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-sand-200">
            {options.map((o) => (
              <button
                key={o.code}
                type="button"
                role="option"
                aria-selected={o.code === lang}
                onClick={() => { setLang(o.code); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-sand-100 transition ${o.code === lang ? "bg-forest-600/5" : ""}`}
                data-testid={`language-option-${o.code}`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-forest-700 truncate" dir="auto">{o.label}</span>
                  {o.sub && <span className="block text-xs text-muted-foreground truncate">{o.sub}</span>}
                </span>
                {o.code === lang && <Check className="h-4 w-4 text-forest-600 shrink-0" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SelectSwitcher({ className }) {
  const { lang, setLang, options, hasLanguages, t } = useOptions();
  if (!hasLanguages) return null;

  return (
    <label className={`flex items-center gap-2 text-sm ${className}`} data-testid="language-switcher">
      <Languages className="h-4 w-4 text-forest-700 shrink-0" aria-hidden="true" />
      <span className="sr-only">{t("language.label")}</span>
      <select
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        data-testid="language-select"
        className="flex-1 min-w-0 rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600/30"
      >
        {options.map((o) => (
          <option key={o.code} value={o.code}>{o.sub ? `${o.label} (${o.sub})` : o.label}</option>
        ))}
      </select>
    </label>
  );
}

export default function LanguageSwitcher({ className = "", variant = "select" }) {
  return variant === "icon" ? <IconSwitcher /> : <SelectSwitcher className={className} />;
}
